
import Vue, { PropType } from 'vue';
import _trim from 'lodash/trim';
import { mapActions, mapState } from 'vuex';
import {
	FeedbackReport,
	FeedbackReportComment,
	FeedbackReportCommentShot,
	FEEDBACK_REPORT_COMMENT_MAX_LENGTH,
} from '@run-diffusion/shared';
import { SNACKBAR_STATUS } from '@/constants/constants';
import DoubleConfirmDangerDialog from '@/components/base/DoubleConfirmDangerDialog.vue';
import FeedbackShotViewer from './FeedbackShotViewer.vue';
import { feedbackFileUrl } from './feedbackFiles';
import { commentLengthRule, isCommentDeleted, statusLabel } from './feedbackReports';
import { deleteComment, editComment, feedbackApiFailure } from './feedbackWrites';

// One comment, status line or reply on a feedback report, with the screenshots that came with it.
// Its author can change a comment's words, or delete it, which stamps deletedAt: the comment
// stays, as "Comment deleted", with its replies under it (restServer holds both to the author,
// and removes nothing). Status lines, and what an agent wrote, stay as they were written.
export default Vue.extend({
	name: 'FeedbackCommentItem',
	props: {
		report: { type: Object as PropType<FeedbackReport>, required: true },
		comment: { type: Object as PropType<FeedbackReportComment>, required: true },
		reply: { type: Boolean, default: false },
		canReply: { type: Boolean, default: false },
	},
	data () {
		return {
			editing: false,
			draft: '',
			saving: false,
			deleting: false,
			shotUrls: [] as string[],
			// the screenshot open full size, or null
			viewing: null as number | null,
			rules: [commentLengthRule],
			isMac: /Mac|iPhone|iPad/.test(navigator.platform || ''),
		};
	},
	computed: {
		...mapState([
			'user',
		]),
		deleted (): boolean {
			return isCommentDeleted(this.comment);
		},
		// its author's to edit or delete, until it is deleted
		isMine (): boolean {
			// an agent's comment carries its developer's uid, but stays as the agent wrote it (firestore.rules)
			return this.comment.kind === 'comment' && !this.deleted && !!this.user && this.comment.author.uid === this.user.id && this.comment.author.kind !== 'agent';
		},
		shots (): FeedbackReportCommentShot[] {
			return this.comment.shots || [];
		},
		canSave (): boolean {
			const body: string = _trim(this.draft || '');
			return (!!body || this.shots.length > 0) && body.length <= FEEDBACK_REPORT_COMMENT_MAX_LENGTH && body !== this.comment.body && !this.saving;
		},
		commentTime (): string {
			const at: Date | null = this.timeOf(this.comment.createdAt);
			return at ? at.toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : 'just now';
		},
		editedTitle (): string {
			const at: Date | null = this.timeOf(this.comment.editedAt);
			return at ? `Edited ${at.toLocaleString()}` : 'Edited';
		},
	},
	watch: {
		shots: {
			immediate: true,
			async handler (shots: FeedbackReportCommentShot[]) {
				this.shotUrls = await Promise.all(shots.map(async (shot: FeedbackReportCommentShot) => {
					try {
						return await feedbackFileUrl(shot.path);
					} catch (e) {
						console.error('[feedback] a comment\'s screenshot failed to load', shot.path, e);
						return '';
					}
				}));
			},
		},
	},
	methods: {
		...mapActions([
			'updateSnackbar',
		]),
		statusLabel,
		timeOf (timestamp: { toDate: () => Date } | null | undefined): Date | null {
			return timestamp ? timestamp.toDate() : null;
		},
		startEdit () {
			this.draft = this.comment.body;
			this.editing = true;
			this.$nextTick(() => {
				const editor = this.$refs.editor as Vue | undefined;
				const input: HTMLTextAreaElement | null = editor ? editor.$el.querySelector('textarea') : null;
				if (!input) return;
				input.focus();
				input.setSelectionRange(input.value.length, input.value.length);
			});
		},
		cancelEdit () {
			this.editing = false;
			this.draft = '';
		},
		async save () {
			if (!this.canSave) return;
			this.saving = true;
			try {
				const failure = feedbackApiFailure(await editComment(this.report.number, this.comment.id, _trim(this.draft)));
				if (failure) {
					console.error('[feedback] failed to edit a comment', this.comment.id, failure.code);
					this.updateSnackbar({ status: SNACKBAR_STATUS.ERROR, message: failure.message, show: true });
					return;
				}
				this.editing = false;
			} finally {
				this.saving = false;
			}
		},
		async remove (close: () => void) {
			this.deleting = true;
			try {
				const failure = feedbackApiFailure(await deleteComment(this.report.number, this.comment.id));
				if (failure) {
					console.error('[feedback] failed to delete a comment', this.comment.id, failure.code);
					this.updateSnackbar({ status: SNACKBAR_STATUS.ERROR, message: failure.message, show: true });
					return;
				}
				this.updateSnackbar({ status: SNACKBAR_STATUS.SUCCESS, message: 'Comment deleted', show: true });
			} finally {
				this.deleting = false;
				close();
			}
		},
	},
	components: {
		DoubleConfirmDangerDialog,
		FeedbackShotViewer,
	},
});
